import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronIcon, ClockIcon, Field, LoadingState, MenuButton, Modal, MoonIcon, PlayIcon, Select, SunIcon, Switch, TelegramIcon, useConfirm, useToast, type MenuItem } from "@signet/ui";
import { ApiError, useMe } from "@signet/web-kit";
import { fetchTodos, telegramApi, todoKeys, updateTodo, type Delivery, type TelegramStatus, type Todo } from "./api.ts";
import { blockedBecause, type DayState, type Schedule } from "../shared/day.ts";
import { DAY_CHANGED } from "./reminders.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Jay's Telegram in To-do (2026-10-08). Ticking off the to-do he is working
   on asks what is next and posts "✅ Done - ... / > Moved onto
   ...". A Work on this button switches, editing that message when it can
   (the rules are in server/telegram/announce.ts). Only jayxoga sees any of
   it; the server answers 404 to anyone else.
   ────────────────────────────────────────────────────────────────────────── */

const TELEGRAM_PEOPLE = new Set(["jayxoga"]);

const messageOf = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");

/** Telegram status for this person, or null when it is not theirs to use. */
export function useTelegram(): TelegramStatus | null {
  const { me } = useMe();
  const mine = Boolean(me && TELEGRAM_PEOPLE.has(me.user.username.toLowerCase()));
  const status = useQuery({
    queryKey: todoKeys.telegram,
    queryFn: telegramApi.status,
    enabled: mine,
    staleTime: 30_000,
    // Keeps the Status menu right across midnight without a reload.
    refetchInterval: 60_000,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  });
  // A status sent from the notch moves the day on: the menu follows at once.
  const client = useQueryClient();
  useEffect(() => {
    if (!mine) return;
    const changed = () => void client.invalidateQueries({ queryKey: todoKeys.all });
    window.addEventListener(DAY_CHANGED, changed);
    return () => window.removeEventListener(DAY_CHANGED, changed);
  }, [mine, client]);
  return mine ? status.data ?? null : null;
}

/** Says what happened on Telegram, and offers Retry when the message didn't go. */
export function useDeliveryNote() {
  const toast = useToast();
  const client = useQueryClient();
  const note = (delivery: Delivery | undefined) => {
    if (delivery === "edited") toast.success("Edited the last Telegram message");
    if (delivery === "sent") toast.success("Posted to Telegram");
    if (delivery === "failed") {
      toast.info("The Telegram message didn't go", "The to-do itself is saved.", {
        label: "Retry",
        onSelect: () => {
          telegramApi
            .retry()
            .then((r) => note(r.telegram === "failed" ? undefined : r.telegram))
            .catch((error) => toast.error("Couldn't retry", messageOf(error)))
            .finally(() => void client.invalidateQueries({ queryKey: todoKeys.telegram }));
        },
      });
    }
  };
  return note;
}

/** Switch to working on a to-do. */
export function useWorkOn() {
  const client = useQueryClient();
  const toast = useToast();
  const note = useDeliveryNote();
  return useMutation({
    mutationFn: (todoId: string) => telegramApi.work(todoId),
    onSuccess: (result) => {
      note(result.telegram);
      void client.invalidateQueries({ queryKey: todoKeys.all });
    },
    onError: (error) => toast.error("Couldn't switch", messageOf(error)),
  });
}

/** The row's tick-off animation (tick.tsx): played while the save goes, put back if it fails. */
type Leave = { play: () => Promise<void>; undo: () => void };

/**
 * Ticking a to-do on and off. For Jay, ticking off the one he is working on
 * first asks what is next; everything else ticks straight away. Ticking off
 * plays the row's animation and offers Undo, since the to-do leaves the list.
 */
export function useTick(todo: Todo, leave?: Leave): { tick: () => void; pending: boolean; picker: ReactNode } {
  const telegram = useTelegram();
  const client = useQueryClient();
  const toast = useToast();
  const note = useDeliveryNote();
  const [asking, setAsking] = useState(false);
  const [pending, setPending] = useState(false);
  const refresh = () => void client.invalidateQueries({ queryKey: todoKeys.all });

  const save = async (patch: { done: boolean; next?: string | null }) => {
    setPending(true);
    const leaving = patch.done && !todo.done && leave;
    try {
      // The list is read again only after both, so the row isn't unmounted mid-swipe.
      const [result] = await Promise.all([updateTodo(todo.id, patch), leaving ? leave.play() : null]);
      note(result.telegram);
      if (leaving) {
        toast.success("Done", todo.name, {
          label: "Undo",
          onSelect: () => {
            updateTodo(todo.id, { done: false })
              .catch((error) => toast.error("Couldn't undo it", messageOf(error)))
              .finally(refresh);
          },
        });
      }
    } catch (error) {
      if (leaving) leave.undo();
      toast.error("Couldn't save that", messageOf(error));
    } finally {
      setPending(false);
      refresh();
    }
  };

  const tick = () => {
    if (!todo.done && telegram?.connected && telegram.working === todo.id) setAsking(true);
    else void save({ done: !todo.done });
  };
  const picker = asking ? (
    <NextPicker
      todo={todo}
      onPick={(next) => {
        setAsking(false);
        void save({ done: true, next });
      }}
      onCancel={() => setAsking(false)}
    />
  ) : null;
  return { tick, pending, picker };
}

function NextPicker({ todo, onPick, onCancel }: { todo: Todo; onPick: (next: string | null) => void; onCancel: () => void }) {
  const mine = useQuery({ queryKey: todoKeys.list("mine"), queryFn: () => fetchTodos("mine") });
  const choices = (mine.data?.items ?? []).filter((t) => !t.done && t.id !== todo.id);
  // Opens once the list is in, so the first to-do takes the focus and Enter picks it.
  if (mine.isLoading) return null;
  return (
    <Modal
      open
      onClose={onCancel}
      title="What's next?"
      description={`Ticking off "${todo.name}". Pick what you're moving on to.`}
      footer={
        <>
          <button type="button" className="secondary-button" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="secondary-button" onClick={() => onPick(null)} {...(choices.length ? {} : { "data-autofocus": true })}>
            Nothing for now
          </button>
        </>
      }
    >
      {mine.data && !choices.length ? <p className="td-faint">Nothing else is given to you.</p> : null}
      <ul className="td-next" aria-label="Your open to-dos">
        {choices.map((t, i) => (
          <li key={t.id}>
            <button type="button" className="td-next-option" onClick={() => onPick(t.id)} {...(i === 0 ? { "data-autofocus": true } : {})}>
              <span>{t.name}</span>
              {t.creator ? <span className="td-next-creator">{t.creator}</span> : null}
            </button>
          </li>
        ))}
      </ul>
    </Modal>
  );
}

const STATUS_ITEMS = [
  { status: "start", label: "Starting work", icon: <SunIcon size={15} /> },
  { status: "lunch", label: "Running on lunch", icon: <ClockIcon size={15} /> },
  { status: "back", label: "Back from lunch", icon: <PlayIcon size={15} /> },
  { status: "finish", label: "Finishing work", icon: <MoonIcon size={15} /> },
] as const;

const DAY_LABEL: Record<DayState, string> = { off: "Not started", working: "Working", lunch: "At lunch", finished: "Finished" };

/**
 * The day's markers, posted to the topic in one click (Jay, 2026-10-08).
 * Only what fits next can be picked, with the reason under the rest
 * (shared/day.ts; the server checks the same rules). Finishing work adds
 * every open to-do given to him under TOMORROW, in list order, and stops the
 * "working on" marker.
 */
export function StatusMenu() {
  const telegram = useTelegram();
  const toast = useToast();
  const client = useQueryClient();
  if (!telegram?.connected || !telegram.topic) return null;
  const day = telegram.day;

  const say = (status: (typeof STATUS_ITEMS)[number]["status"], label: string, quiet = false) => {
    telegramApi
      .say(status, quiet)
      .then((r) => {
        client.setQueryData<TelegramStatus>(todoKeys.telegram, (old) => (old ? { ...old, day: r.day } : old));
        if (quiet) toast.success("Marked as started", "Nothing was posted to Telegram.");
        else if (r.telegram === "sent") toast.success(`Posted: ${label}`);
        else toast.info(`"${label}" didn't go`, "Telegram didn't take it.", { label: "Retry", onSelect: () => say(status, label) });
      })
      .catch((error) => toast.error(`Couldn't post "${label}"`, messageOf(error)))
      .finally(() => window.dispatchEvent(new Event(DAY_CHANGED)));
  };
  const items: MenuItem[] = STATUS_ITEMS.flatMap((s): MenuItem[] => {
    const blocked = blockedBecause(s.status, day);
    const item: MenuItem = { kind: "item", label: s.label, icon: s.icon, disabled: Boolean(blocked), note: blocked ?? undefined, onSelect: () => say(s.status, s.label) };
    // Started hours ago and forgot: mark it without a late message (Jay, 2026-11-05).
    if (s.status !== "start" || blocked) return [item];
    return [item, { kind: "item", label: "Already started", icon: <SunIcon size={15} />, note: "Marks you as working, posts nothing", onSelect: () => say(s.status, s.label, true) }];
  });

  return (
    <MenuButton items={items} label={`Post a status to Telegram. Now: ${DAY_LABEL[day.state]}`} className={`secondary-button td-status-button td-day-${day.state}`}>
      <span className="td-day-dot" aria-hidden="true" />
      {DAY_LABEL[day.state]} <ChevronIcon size={12} />
    </MenuButton>
  );
}

/** The To-do page's way in, for Jay only. */
export function TelegramLink() {
  const telegram = useTelegram();
  if (!telegram) return null;
  return (
    <Link to={"/todo/telegram" as never} className="secondary-button td-telegram-link">
      <TelegramIcon size={15} />
      {telegram.connected ? (telegram.topic ? telegram.topic.title : "Pick a topic") : "Connect Telegram"}
    </Link>
  );
}

// ── the setup page ──────────────────────────────────────────────────────────

export function TelegramPage() {
  const { me } = useMe();
  const mine = Boolean(me && TELEGRAM_PEOPLE.has(me.user.username.toLowerCase()));
  const status = useQuery({ queryKey: todoKeys.telegram, queryFn: telegramApi.status, enabled: mine, retry: false });

  return (
    <div className="page td-page td-telegram">
      <header className="page-head">
        <div>
          <h1>Telegram</h1>
          <p>Ticking off and switching to-dos posts in your forum topic, as you.</p>
        </div>
        <Link to={"/todo" as never} className="secondary-button">
          Back to To-do
        </Link>
      </header>
      {!mine || status.error ? <p className="td-faint">Not found.</p> : null}
      {mine && status.isLoading ? <LoadingState label="Checking Telegram" /> : null}
      {status.data ? (status.data.connected ? <Connected status={status.data} /> : <SignIn status={status.data} />) : null}
    </div>
  );
}

function SignIn({ status }: { status: TelegramStatus }) {
  const client = useQueryClient();
  const [apiId, setApiId] = useState("");
  const [apiHash, setApiHash] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [stage, setStage] = useState<"keys" | "code" | "password">("keys");
  const [error, setError] = useState<string | null>(null);
  const [viaApp, setViaApp] = useState(true);

  const send = useMutation({
    mutationFn: () => telegramApi.sendCode({ phone, ...(apiId ? { apiId: Number(apiId) } : {}), ...(apiHash ? { apiHash: apiHash.trim() } : {}) }),
    onSuccess: (r) => {
      setError(null);
      setViaApp(r.viaApp);
      setStage("code");
    },
    onError: (e) => setError(messageOf(e)),
  });
  const signIn = useMutation({
    mutationFn: () => telegramApi.signIn({ code, ...(password ? { password } : {}) }),
    onSuccess: (r) => {
      setError(null);
      if ("needsPassword" in r) setStage("password");
      else {
        client.setQueryData(todoKeys.telegram, r);
        void client.invalidateQueries({ queryKey: todoKeys.telegram });
      }
    },
    onError: (e) => setError(messageOf(e)),
  });

  return (
    <section className="panel td-telegram-card">
      <div className="panel-body">
        {stage === "keys" ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send.mutate();
            }}
          >
            <p className="td-faint">
              The API ID and hash come from my.telegram.org, under API development tools. They are stored encrypted and never shown again.
            </p>
            <Field label="API ID" hint={status.hasKeys ? "Already saved. Leave empty to keep it." : undefined}>
              <input value={apiId} onChange={(e) => setApiId(e.target.value.replace(/\D/g, ""))} inputMode="numeric" autoComplete="off" required={!status.hasKeys} />
            </Field>
            <Field label="API hash" hint={status.hasKeys ? "Already saved. Leave empty to keep it." : undefined}>
              <input type="password" value={apiHash} onChange={(e) => setApiHash(e.target.value)} autoComplete="off" spellCheck={false} required={!status.hasKeys} />
            </Field>
            <Field label="Phone number" hint="With the country code, like +61 4...">
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" required />
            </Field>
            {error ? <p className="td-error">{error}</p> : null}
            <button type="submit" className="button brass" disabled={send.isPending}>
              {send.isPending ? "Sending..." : "Send code"}
            </button>
          </form>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              signIn.mutate();
            }}
          >
            <Field label="Code" hint={viaApp ? "Telegram sent it to your Telegram app." : "Telegram sent it by text message."}>
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} inputMode="numeric" autoComplete="one-time-code" autoFocus required />
            </Field>
            {stage === "password" ? (
              <Field label="Two-step password" hint="Your account has two-step verification on.">
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoFocus required />
              </Field>
            ) : null}
            {error ? <p className="td-error">{error}</p> : null}
            <div className="td-telegram-actions">
              <button type="submit" className="button brass" disabled={signIn.isPending}>
                {signIn.isPending ? "Signing in..." : "Sign in"}
              </button>
              <button type="button" className="secondary-button" onClick={() => setStage("keys")}>
                Start again
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function Connected({ status }: { status: TelegramStatus }) {
  const client = useQueryClient();
  const toast = useToast();
  const { confirm, element } = useConfirm();
  const [chat, setChat] = useState<string | null>(status.chat?.id ?? null);
  const [topic, setTopic] = useState<number | null>(status.topic?.id ?? null);
  const [watch, setWatch] = useState(status.watch);
  const groups = useQuery({ queryKey: [...todoKeys.telegram, "groups"], queryFn: telegramApi.groups, staleTime: 5 * 60_000, retry: 1 });
  const topics = useQuery({ queryKey: [...todoKeys.telegram, "topics", chat], queryFn: () => telegramApi.topics(chat!), enabled: Boolean(chat), staleTime: 5 * 60_000, retry: 1 });

  const save = useMutation({
    mutationFn: () => telegramApi.target({ chatId: chat!, topicId: topic!, watch }),
    onSuccess: (r) => {
      client.setQueryData(todoKeys.telegram, r);
      toast.success("Saved", `Messages go to ${r.topic?.title ?? "the topic"}.`);
    },
    onError: (e) => toast.error("Couldn't save that", messageOf(e)),
  });
  const signOut = useMutation({
    mutationFn: telegramApi.signOut,
    onSuccess: (r) => client.setQueryData(todoKeys.telegram, r),
    onError: (e) => toast.error("Couldn't sign out", messageOf(e)),
  });

  const changed = chat !== (status.chat?.id ?? null) || topic !== (status.topic?.id ?? null) || watch.replace(/^@/, "") !== status.watch;

  return (
    <section className="panel td-telegram-card">
      {element}
      <div className="panel-body">
        <p className="td-telegram-who">
          <TelegramIcon size={16} /> Signed in as <strong>{status.account ?? "you"}</strong>
        </p>
        {status.chat && status.topic ? (
          <p className="td-faint">
            Posting to {status.topic.title} in {status.chat.title}.
          </p>
        ) : (
          <p className="td-error">Pick the group and topic to post to. Nothing is posted until you do.</p>
        )}
        <Field label="Group">
          <Select
            label="Group"
            value={chat}
            options={(groups.data?.groups ?? []).map((g) => ({ value: g.id, label: g.title }))}
            onChange={(next) => {
              setChat(next);
              setTopic(null);
            }}
            searchable
            placeholder={groups.isError ? "Couldn't reach Telegram. Try again shortly." : groups.isLoading ? "Loading your groups..." : "Pick a forum group"}
          />
        </Field>
        <Field label="Topic">
          <Select
            label="Topic"
            value={topic === null ? null : String(topic)}
            options={(topics.data?.topics ?? []).map((t) => ({ value: String(t.id), label: t.title }))}
            onChange={(next) => setTopic(next ? Number(next) : null)}
            searchable
            disabled={!chat}
            placeholder={topics.isError ? "Couldn't load the topics." : topics.isLoading && chat ? "Loading topics..." : "Pick a topic"}
          />
        </Field>
        <Field
          label="Whose reading counts"
          hint={
            status.watchFound || changed
              ? "After five minutes, Work on this still edits the last message while this person hasn't read it."
              : "Telegram couldn't find this username, so after five minutes a switch always posts a new message."
          }
        >
          <input value={watch} onChange={(e) => setWatch(e.target.value)} autoComplete="off" spellCheck={false} />
        </Field>
        <div className="td-telegram-actions">
          <button type="button" className="button brass" disabled={!chat || !topic || !changed || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            className="secondary-button"
            disabled={signOut.isPending}
            onClick={async () => {
              if (await confirm({ title: "Sign out of Telegram?", body: "Signet stops posting, and this session is removed from your Telegram devices.", confirmLabel: "Sign out", danger: true })) signOut.mutate();
            }}
          >
            Sign out
          </button>
        </div>
        <Reminders schedule={status.schedule} />
      </div>
    </section>
  );
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Times in quarter hours, picked from the kit's list rather than a native time input (house rule).
const TIMES = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  return { value, label: `${h % 12 || 12}:${String(m).padStart(2, "0")}${h < 12 ? "am" : "pm"}` };
});
const span = (minutes: number) => [Math.floor(minutes / 60) ? `${Math.floor(minutes / 60)}h` : "", minutes % 60 ? `${minutes % 60}m` : ""].filter(Boolean).join(" ");
const LENGTHS = Array.from({ length: 19 }, (_, i) => 30 + i * 15).map((m) => ({ value: String(m), label: span(m) }));
/** A saved time that is not on the quarter hour still shows. */
const timeOptions = (current: string) => (TIMES.some((t) => t.value === current) ? TIMES : [...TIMES, { value: current, label: current }]);

/** When the notch suggests each status, in the desktop app. Saved as soon as it changes. */
function Reminders({ schedule }: { schedule: Schedule }) {
  const client = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState(schedule);
  useEffect(() => setDraft(schedule), [schedule]);
  const save = useMutation({
    mutationFn: telegramApi.schedule,
    onSuccess: (r) => {
      client.setQueryData(todoKeys.telegram, r);
      window.dispatchEvent(new Event(DAY_CHANGED));
    },
    onError: (e) => {
      setDraft(schedule);
      toast.error("Couldn't save the reminders", messageOf(e));
    },
  });
  const change = (patch: Partial<Schedule>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    save.mutate(next);
  };
  const lengths = LENGTHS.some((l) => Number(l.value) === draft.lunchMinutes) ? LENGTHS : [...LENGTHS, { value: String(draft.lunchMinutes), label: span(draft.lunchMinutes) }];

  return (
    <fieldset className="td-reminders">
      <legend>Notch reminders</legend>
      <div className="td-reminders-on">
        <Switch checked={draft.on} onChange={(on) => change({ on })} label="Notch reminders" />
        <span>Suggest each status from the notch in the Signet Mac app</span>
      </div>
      <div className="td-reminders-grid">
        <Field label="Starting work">
          <Select label="Starting work" value={draft.start} options={timeOptions(draft.start)} onChange={(v) => v && change({ start: v })} disabled={!draft.on} searchable />
        </Field>
        <Field label="Running on lunch">
          <Select label="Running on lunch" value={draft.lunch} options={timeOptions(draft.lunch)} onChange={(v) => v && change({ lunch: v })} disabled={!draft.on} searchable />
        </Field>
        <Field label="Finishing work">
          <Select label="Finishing work" value={draft.finish} options={timeOptions(draft.finish)} onChange={(v) => v && change({ finish: v })} disabled={!draft.on} searchable />
        </Field>
        <Field label="Lunch lasts" hint={`Back from lunch comes up ${span(draft.lunchMinutes)} after you go.`}>
          <Select label="Lunch lasts" value={String(draft.lunchMinutes)} options={lengths} onChange={(v) => v && change({ lunchMinutes: Number(v) })} disabled={!draft.on} />
        </Field>
      </div>
      <div className="td-reminders-days" role="group" aria-label="Days">
        {WEEKDAYS.map((name, i) => {
          const day = i + 1;
          const on = draft.days.includes(day);
          return (
            <button
              key={name}
              type="button"
              className={on ? "on" : ""}
              aria-pressed={on}
              disabled={!draft.on}
              onClick={() => change({ days: on ? draft.days.filter((d) => d !== day) : [...draft.days, day].sort() })}
            >
              {name}
            </button>
          );
        })}
      </div>
      <p className="td-faint">Back from lunch comes up on any day, and stays until you send it.</p>
    </fieldset>
  );
}
